
import type { Property } from '../mocks/properties';
import { resolveApiBaseUrl } from './apiClient';
import { getAmountFormatter, latnLocale } from './numberFormat';

const DEFAULT_CURRENCY = 'SAR';
const SAR_SYMBOL = 'SAR';

/**
 * Resolve image URL for a relative path. Images are served from the host ROOT
 * (/uploads, /property_images), NOT under the API's /api prefix — so resolve
 * against the origin (strip a trailing /api) to avoid /api/uploads 404s.
 */
export function resolveImageUrl(imageUrl: string | undefined): string {
  if (!imageUrl) return '';
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  const origin = (resolveApiBaseUrl() || '').replace(/\/+$/, '').replace(/\/api$/, '');
  const path = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
  return `${origin}${path}`;
}

function getCurrentLocale(): string {
  if (typeof document !== 'undefined') {
    const lang = document.documentElement.lang;
    if (lang && lang.trim().length > 0) {
      return lang;
    }
  }

  if (typeof navigator !== 'undefined' && navigator.language) {
    return navigator.language;
  }

  return 'en-US';
}

export interface FormatPriceOptions {
  locale?: string;
  currency?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  currencySymbol?: string;
}

export const formatPrice = (price: number, options: FormatPriceOptions = {}): string => {
  const locale = options.locale ?? getCurrentLocale();
  const currency = options.currency ?? DEFAULT_CURRENCY;
  const minimumFractionDigits = options.minimumFractionDigits ?? 0;
  const maximumFractionDigits = options.maximumFractionDigits ?? 0;

  if (currency === DEFAULT_CURRENCY) {
    // Western digits with "," grouping in both languages (utils/numberFormat.ts);
    // `locale` still decides where the symbol goes.
    const numberFormatter = getAmountFormatter({
      minimumFractionDigits,
      maximumFractionDigits,
    });
    const formattedNumber = numberFormatter.format(price);
    const symbol = options.currencySymbol ?? SAR_SYMBOL;
    const separator = '\u00A0';

    if (locale.toLowerCase().startsWith('ar')) {
      return `${formattedNumber}${separator}${symbol}`;
    }

    return `${symbol}${separator}${formattedNumber}`;
  }

  const formatter = new Intl.NumberFormat(latnLocale(locale), {
    style: 'currency',
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  });
  return formatter.format(price);
};

/**
 * Western digits with "," grouping whatever the UI language («2,650,000», never
 * «٢٬٦٥٠٬٠٠٠»). The locale argument is kept so existing call sites compile; it no
 * longer changes the digits.
 */
export const formatNumber = (value: number, _locale?: string): string => {
  return getAmountFormatter().format(value);
};

/**
 * True when a property has no usable price (0 / null / negative) — e.g. land
 * listed "price on request". Lets the UI show a label instead of "0".
 */
export const isPriceOnRequest = (price?: number | null): boolean => !price || price <= 0;

/** Localized "Price on request" label for price-less listings. */
export const priceOnRequestLabel = (isRTL: boolean): string =>
  isRTL ? 'السعر عند الطلب' : 'Price on request';

export const formatDate = (dateString: string, locale: string = getCurrentLocale()): string => {
  const date = new Date(dateString);
  // Same language and calendar as `locale`, Latin digits (numerals policy).
  return date.toLocaleDateString(latnLocale(locale), {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const calculatePricePerSqft = (price: number, sqft: number): number => {
  return Math.round(price / sqft);
};

export const getPropertyStatusColor = (status: Property['status']): string => {
  switch (status) {
    // These pills sit on top of a listing photo, so they are tinted glass over
    // an unknown background — light text on a translucent wash of their own hue.
    // THEME.md §2.4c: `pending` is the gold slot (G8), `sold` the red one (R4).
    // `rented` moves off sky-blue, which §3.6 bars from the interface.
    case 'available':
      return 'text-emerald-100 bg-emerald-500/20 border border-emerald-300/40 backdrop-blur';
    case 'pending':
      return 'text-[#FEF8E8] bg-[#FFC107]/25 border border-[#FBD35A]/50 backdrop-blur';
    case 'sold':
      return 'text-[#FDE8E7] bg-[#DF2F2B]/30 border border-[#DF2F2B]/60 backdrop-blur';
    case 'rented':
      return 'text-[#DDEEEB] bg-[#004D40]/35 border border-[#7AC3B7]/50 backdrop-blur';
    default:
      return 'text-slate-100 bg-slate-500/20 border border-slate-300/40 backdrop-blur';
  }
};

// getPropertyTypeIcon removed — use getPropertyTypeIconId from config/propertyTypeCatalog
// with <DomainIcon>, the unified domain icon registry.

export { getCurrentLocale };
